import { useState } from "react";
import { motion } from "framer-motion";
import { TicketPercent, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/landing/Reveal";

export const Voucher = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/voucher/claim", {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
      });
      setDone(true);
      toast.success(
        data.duplicate
          ? "You've already got a $10 voucher on file — we'll apply it!"
          : "$10 voucher claimed! We'll call to schedule your pickup."
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong — please call 480-940-6023.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="voucher-section" className="bg-gradient-to-r from-fall-amber via-amber-400 to-fall-amber py-10">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <Reveal>
          <div className="flex flex-col items-center gap-6 lg:flex-row lg:justify-between">
            <div className="flex items-center gap-4 text-center lg:text-left">
              <motion.span
                animate={{ rotate: [0, -8, 8, 0] }}
                transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut" }}
                className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand-navy text-fall-amber shadow-lg sm:flex"
              >
                <TicketPercent className="h-7 w-7" />
              </motion.span>
              <div>
                <p className="font-display text-xl font-extrabold text-brand-navy sm:text-2xl">
                  New here? Take <span className="underline decoration-brand-navy/40 decoration-4 underline-offset-4">$10 off</span> your first order
                </p>
                <p className="mt-0.5 text-sm font-semibold text-brand-navy/70">
                  On top of free pickup &amp; delivery — one per new customer.
                </p>
              </div>
            </div>

            {done ? (
              <p data-testid="voucher-success" className="flex items-center gap-2 rounded-2xl bg-brand-navy px-6 py-4 font-display text-sm font-extrabold uppercase tracking-widest text-fall-amber">
                <CheckCircle2 className="h-5 w-5" /> Voucher claimed — we&apos;ll call you!
              </p>
            ) : (
              <form data-testid="voucher-form" onSubmit={submit} className="flex w-full max-w-xl flex-col gap-2 sm:flex-row">
                <input
                  data-testid="voucher-name-input"
                  required
                  minLength={2}
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-xl border-2 border-brand-navy/20 bg-white px-4 py-3 text-sm font-semibold text-brand-navy placeholder:text-slate-400 focus:border-brand-navy focus:outline-none"
                />
                <input
                  data-testid="voucher-phone-input"
                  required
                  minLength={7}
                  type="tel"
                  placeholder="Phone number"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full rounded-xl border-2 border-brand-navy/20 bg-white px-4 py-3 text-sm font-semibold text-brand-navy placeholder:text-slate-400 focus:border-brand-navy focus:outline-none"
                />
                <input
                  data-testid="voucher-email-input"
                  type="email"
                  placeholder="Email (optional)"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-xl border-2 border-brand-navy/20 bg-white px-4 py-3 text-sm font-semibold text-brand-navy placeholder:text-slate-400 focus:border-brand-navy focus:outline-none"
                />
                <button
                  data-testid="voucher-claim-button"
                  type="submit"
                  disabled={sending}
                  className="shrink-0 rounded-xl bg-brand-navy px-6 py-3 font-display text-xs font-extrabold uppercase tracking-widest text-white shadow-lg transition-transform hover:scale-105 disabled:opacity-60"
                >
                  {sending ? "Claiming…" : "Claim $10"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
